import {
  ApiError,
  ProvisioningRequest,
  User,
  ResellerUser,
  CreateUserData,
  UpdateUserData,
  UpdateResellerProfileData,
  AccountsResponse,
  AccountDetailResponse,
  RefreshResponse,
  CancellationActionResponse,
  InvoicePreviewResponse,
  Invoice,
  BillingSettings,
  ResellerWholesalePercent,
  ImpersonationContext,
  StartImpersonationResponse,
  StopImpersonationResponse,
  AssetPage,
  PartnerAsset,
  CreateAssetData,
  UpdateAssetData,
  AssetsResponse,
  AdminAssetsResponse,
  UploadUrlResponse,
  DownloadUrlResponse,
  SfPartnerLookup,
  Referral,
  ReferralsResponse,
  ReferralToolkit,
  ReferralRefreshResponse,
  EarningsResponse,
  CommissionStatement,
  CommissionTerms,
  CommissionTermsInput,
  AdminPartner,
  AdminPartnerDetail,
  RecomputeResult,
} from '@/types';

// =============================================================================
// API CLIENT SECURITY NOTES
//
// XSS PREVENTION:
// - All API responses are treated as data, not HTML
// - React's JSX automatically escapes rendered values
// - Never use dangerouslySetInnerHTML with API data
// - Responses are JSON.parsed, not eval'd or innerHTML'd
//
// TOKEN HANDLING:
// - Token is stored in localStorage (see useAuth.tsx for migration notes)
// - Token is sent via Authorization header, not in URL
// - Token is cleared on logout and auth failures
//
// FUTURE HttpOnly COOKIE MIGRATION:
// When migrating to HttpOnly cookies:
// 1. Remove token property and setToken method
// 2. Add credentials: 'include' to all fetch options
// 3. Remove Authorization header construction
// 4. Add CSRF token handling if implementing CSRF protection
// =============================================================================

const API_BASE = '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw data as ApiError;
    }

    return data;
  }

  // Auth
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getMe(): Promise<{ user: User; impersonation?: ImpersonationContext }> {
    return this.request('/auth/me');
  }

  // Reseller Requests
  async createRequest(payload: Record<string, unknown>): Promise<ProvisioningRequest> {
    return this.request('/requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateRequest(id: string, payload: Record<string, unknown>): Promise<ProvisioningRequest> {
    return this.request(`/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async submitRequest(id: string): Promise<{ id: string; status: string }> {
    return this.request(`/requests/${id}/submit`, {
      method: 'POST',
    });
  }

  async getRequests(status?: string): Promise<{ requests: ProvisioningRequest[] }> {
    const params = status ? `?status=${status}` : '';
    return this.request(`/requests${params}`);
  }

  async getRequest(id: string): Promise<ProvisioningRequest> {
    return this.request(`/requests/${id}`);
  }

  // Admin
  async getAdminRequests(status?: string, search?: string): Promise<{ requests: ProvisioningRequest[] }> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const queryString = params.toString();
    return this.request(`/admin/requests${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminRequest(id: string): Promise<ProvisioningRequest> {
    return this.request(`/admin/requests/${id}`);
  }

  async approveRequest(id: string): Promise<{ id: string; status: string; message: string; error?: string }> {
    return this.request(`/admin/requests/${id}/approve`, {
      method: 'POST',
    });
  }

  async rejectRequest(id: string, reason?: string): Promise<{ id: string; status: string }> {
    return this.request(`/admin/requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async retryRequest(id: string): Promise<{ id: string; status: string; message: string; error?: string }> {
    return this.request(`/admin/requests/${id}/retry`, {
      method: 'POST',
    });
  }

  async completeRequest(id: string, sfAccountId: string): Promise<{ id: string; status: string; sf_account_id: string }> {
    return this.request(`/admin/requests/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ sf_account_id: sfAccountId }),
    });
  }

  async getStats(): Promise<{ stats: Record<string, number> }> {
    return this.request('/admin/stats');
  }

  // Password Reset
  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * SECURITY FIX: Exchange URL token for session ID immediately on page load.
   * This prevents token leakage via referrer headers, browser history, etc.
   * The session ID is short-lived (10 min) and used for the actual reset.
   */
  async validateResetToken(token: string): Promise<{ session_id: string }> {
    return this.request('/auth/validate-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  /**
   * Reset password using session_id (not the URL token).
   * Call validateResetToken first to exchange the URL token for a session ID.
   */
  async resetPassword(sessionId: string, password: string): Promise<{ message: string }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, password }),
    });
  }

  // Admin User Management
  async getUsers(status?: string, search?: string): Promise<{ users: ResellerUser[] }> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const queryString = params.toString();
    return this.request(`/admin/users${queryString ? `?${queryString}` : ''}`);
  }

  async getUser(id: string): Promise<ResellerUser> {
    return this.request(`/admin/users/${id}`);
  }

  async createUser(data: CreateUserData): Promise<ResellerUser> {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: UpdateUserData): Promise<ResellerUser> {
    return this.request(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateUserPassword(id: string, password: string): Promise<{ message: string }> {
    return this.request(`/admin/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  }

  // Affiliate referrals
  async getReferrals(): Promise<ReferralsResponse> {
    return this.request('/referrals');
  }

  async getReferral(id: string): Promise<{ referral: Referral }> {
    return this.request(`/referrals/${encodeURIComponent(id)}`);
  }

  async getReferralToolkit(): Promise<ReferralToolkit> {
    return this.request('/referrals/toolkit');
  }

  async refreshReferrals(): Promise<ReferralRefreshResponse> {
    return this.request('/referrals/refresh', { method: 'POST' });
  }

  // Affiliate earnings
  async getEarnings(): Promise<EarningsResponse> {
    return this.request('/earnings');
  }

  async getEarningsStatement(id: string): Promise<{ statement: CommissionStatement }> {
    return this.request(`/earnings/statements/${id}`);
  }

  // Admin partners and commissions
  async getPartners(): Promise<{ partners: AdminPartner[] }> {
    return this.request('/admin/partners');
  }

  async getPartner(id: string): Promise<AdminPartnerDetail> {
    return this.request(`/admin/partners/${id}`);
  }

  async updatePartnerTerms(id: string, terms: CommissionTermsInput): Promise<{ terms: CommissionTerms; recompute: RecomputeResult | null; recompute_error?: string }> {
    return this.request(`/admin/partners/${id}/terms`, { method: 'PUT', body: JSON.stringify(terms) });
  }

  async recomputePartnerCommissions(id: string): Promise<RecomputeResult> {
    return this.request(`/admin/partners/${id}/commissions/recompute`, { method: 'POST' });
  }

  async getCommissionStatements(status?: string): Promise<{ statements: CommissionStatement[] }> {
    return this.request(`/admin/commission-statements${status ? `?status=${status}` : ''}`);
  }

  async getCommissionStatement(id: string): Promise<{ statement: CommissionStatement }> {
    return this.request(`/admin/commission-statements/${id}`);
  }

  async approveStatement(id: string): Promise<{ statement: CommissionStatement }> {
    return this.request(`/admin/commission-statements/${id}/approve`, { method: 'POST' });
  }

  async markStatementPaid(id: string, reference: string): Promise<{ statement: CommissionStatement; salesforce: { attempted: number; succeeded: number; failed: string[] } }> {
    return this.request(`/admin/commission-statements/${id}/mark-paid`, { method: 'POST', body: JSON.stringify({ reference }) });
  }

  async voidStatement(id: string): Promise<{ statement: CommissionStatement }> {
    return this.request(`/admin/commission-statements/${id}/void`, { method: 'POST' });
  }

  async recomputeAllCommissions(): Promise<{ partners: number; failed: number }> {
    return this.request('/admin/commission-statements/recompute-all', { method: 'POST' });
  }

  async lookupSfPartner(code: string): Promise<SfPartnerLookup> {
    return this.request(`/admin/partners/sf-lookup?code=${encodeURIComponent(code)}`);
  }

  async updateUserStatus(id: string, status: 'active' | 'disabled'): Promise<{ id: string; status: string; message: string }> {
    return this.request(`/admin/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async updateResellerProfile(id: string, data: UpdateResellerProfileData): Promise<{
    id: string;
    reseller_tier: string;
    reseller_support_name: string;
    reseller_support_phone: string;
    reseller_support_email: string;
    message: string;
  }> {
    return this.request(`/admin/users/${id}/reseller-profile`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Salesforce Account Sync
  async getAccounts(): Promise<AccountsResponse> {
    return this.request('/accounts');
  }

  async getAccount(sfAccountId: string): Promise<AccountDetailResponse> {
    return this.request(`/accounts/${sfAccountId}`);
  }

  async refreshAccounts(): Promise<RefreshResponse> {
    return this.request('/accounts/refresh', {
      method: 'POST',
    });
  }

  // Account Lifecycle Management
  async scheduleCancellation(sfAccountId: string, endDate: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/schedule-cancellation`, {
      method: 'POST',
      body: JSON.stringify({ end_date: endDate }),
    });
  }

  async undoCancellation(sfAccountId: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/undo-cancellation`, {
      method: 'POST',
    });
  }

  async cancelTrial(sfAccountId: string, cancelDate: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/cancel-trial`, {
      method: 'POST',
      body: JSON.stringify({ cancel_date: cancelDate }),
    });
  }

  async undoTrialCancellation(sfAccountId: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/undo-trial-cancellation`, {
      method: 'POST',
    });
  }

  async changeTrialCancellation(sfAccountId: string, cancelDate: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/change-trial-cancellation`, {
      method: 'POST',
      body: JSON.stringify({ cancel_date: cancelDate }),
    });
  }

  async changeSubscription(
    sfAccountId: string,
    aiCallAllowance: number
  ): Promise<{
    success: boolean;
    message: string;
    new_allowance: number;
    next_billing_date: string;
    opportunity_id?: string;
    error?: string;
  }> {
    return this.request(`/accounts/${sfAccountId}/subscription`, {
      method: 'POST',
      body: JSON.stringify({ ai_call_allowance: aiCallAllowance }),
    });
  }

  // ============================================================================
  // Wholesale Invoicing - Reseller
  // ============================================================================

  async getInvoicePreview(period: string): Promise<InvoicePreviewResponse> {
    return this.request(`/invoice-previews/${period}`);
  }

  async refreshInvoicePreview(period: string): Promise<InvoicePreviewResponse> {
    return this.request(`/invoice-previews/${period}/refresh`, {
      method: 'POST',
    });
  }

  async getInvoices(): Promise<{ invoices: Invoice[] }> {
    return this.request('/invoices');
  }

  async getInvoice(invoiceId: string): Promise<{ invoice: Invoice }> {
    return this.request(`/invoices/${invoiceId}`);
  }

  // ============================================================================
  // Wholesale Invoicing - Admin
  // ============================================================================

  async getBillingSettings(): Promise<BillingSettings> {
    return this.request('/admin/settings/billing');
  }

  async updateBillingSettings(wholesalePercentDefault: number): Promise<BillingSettings & { message: string }> {
    return this.request('/admin/settings/billing', {
      method: 'PUT',
      body: JSON.stringify({ wholesale_percent_default: wholesalePercentDefault }),
    });
  }

  async getResellerWholesalePercent(resellerId: string): Promise<ResellerWholesalePercent> {
    return this.request(`/admin/resellers/${resellerId}/wholesale-percent`);
  }

  async updateResellerWholesalePercent(
    resellerId: string,
    wholesalePercentOverride: number | null
  ): Promise<ResellerWholesalePercent & { message: string }> {
    return this.request(`/admin/resellers/${resellerId}/wholesale-percent`, {
      method: 'PUT',
      body: JSON.stringify({ wholesale_percent_override: wholesalePercentOverride }),
    });
  }

  async getAdminInvoicePreview(period: string, resellerId: string): Promise<InvoicePreviewResponse> {
    return this.request(`/admin/invoice-previews/${period}?resellerId=${resellerId}`);
  }

  async refreshAdminInvoicePreview(period: string, resellerId: string): Promise<InvoicePreviewResponse> {
    return this.request(`/admin/invoice-previews/${period}/refresh`, {
      method: 'POST',
      body: JSON.stringify({ resellerId }),
    });
  }

  async issueInvoice(period: string, resellerId: string): Promise<{ invoice: Invoice; message: string }> {
    return this.request(`/admin/invoices/${period}/issue`, {
      method: 'POST',
      body: JSON.stringify({ resellerId }),
    });
  }

  async getAdminInvoices(): Promise<{ invoices: Invoice[] }> {
    return this.request('/admin/invoices');
  }

  async getAdminInvoice(invoiceId: string): Promise<{ invoice: Invoice }> {
    return this.request(`/admin/invoices/${invoiceId}`);
  }

  // ============================================================================
  // Admin Impersonation
  // ============================================================================

  async startImpersonation(resellerId: string): Promise<StartImpersonationResponse> {
    return this.request('/admin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ resellerId }),
    });
  }

  async stopImpersonation(resellerId?: string): Promise<StopImpersonationResponse> {
    return this.request('/admin/impersonate/stop', {
      method: 'POST',
      body: JSON.stringify({ resellerId }),
    });
  }

  // ============================================================================
  // Partner Assets - Reseller
  // ============================================================================

  async getAssets(page: AssetPage): Promise<AssetsResponse> {
    return this.request(`/assets/${page}`);
  }

  async getAssetDownloadUrl(page: AssetPage, assetId: string): Promise<DownloadUrlResponse> {
    return this.request(`/assets/${page}/${assetId}/download`);
  }

  // ============================================================================
  // Partner Assets - Admin
  // ============================================================================

  async getAdminAssets(page?: AssetPage): Promise<AdminAssetsResponse> {
    const queryString = page ? `?page=${page}` : '';
    return this.request(`/admin/assets${queryString}`);
  }

  async getAdminAsset(assetId: string): Promise<{ asset: PartnerAsset; download_url: string | null }> {
    return this.request(`/admin/assets/${assetId}`);
  }

  async getUploadUrl(
    fileName: string,
    contentType: string,
    fileSize: number
  ): Promise<UploadUrlResponse> {
    return this.request('/admin/assets/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        file_name: fileName,
        content_type: contentType,
        file_size: fileSize,
      }),
    });
  }

  async createAsset(data: CreateAssetData): Promise<{ asset: PartnerAsset; message: string }> {
    return this.request('/admin/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAsset(assetId: string, data: UpdateAssetData): Promise<{ asset: PartnerAsset; message: string }> {
    return this.request(`/admin/assets/${assetId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAsset(assetId: string): Promise<{ message: string }> {
    return this.request(`/admin/assets/${assetId}`, {
      method: 'DELETE',
    });
  }

  async reorderAssets(page: AssetPage, assetIds: string[]): Promise<{ message: string }> {
    return this.request(`/admin/assets/${page}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
    });
  }
}

export const api = new ApiClient();
